import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BaseFormComponent } from '@mpt-fo/common/lean-shared/components/base-form/base-form.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CustomFormControlDefinition } from '@mpt-fo/common/shared/utils/custom-form-control';
import { SelectAdaptor } from '@mpt-fo/common/lean-shared/components/input-control/input-control.component';
import { StringHelper } from '@mpt-fo/common/core/helpers/strings.helper';
import { BusinessService } from '@mpt-fo/common/core/services/business.service';
import { ModalOptionsConfig, ModalService } from '@mpt-fo/common/lean-shared/components/nz-modal-service/modal.service';
import { StorageKeys } from '@mpt-fo/common/shared/models/storage-keys';
import { LocalStorageService } from '@mpt-fo/common/core/services/local-storage.service';
import {
    AssociatedBusinessType,
    BUSINESS_CATEGORY_STEPS,
    BusinessClassification,
    BusinessTypesModal,
    LogErrorEventType,
    LogEventStatus,
} from './business-categorization-v3.model';
import { AppConfig } from '@mpt-fo/common/shared/config/app-config';
import { ModalType } from '@mpt-fo/common/kamona/model/kamonal-model';
import { ScreenManagerService } from '@mpt-fo/common/core/services/screen-manager.service';
import { DashboardService } from '@mpt-fo/common/core/services/dashboard.service';
import { UtilityService } from '@mpt-fo/common/core/services/utility.service';
import { OnboardingV2Service } from '@mpt-fo/common/core/services/onboarding-v2.service';
import { DEFAULT_DIALOG_CLASSES, MONIEPOINT_DESIGN_TYPE } from '@mpt-fo/common/shared/constants';
import { DataLayerService, StorefrontUploadEvents } from '@mpt-fo/common/core/services/data-layer-service.service';
import { logger } from '@mpt-fo/common/telemetry';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-business-categorization-v3',
    templateUrl: './business-categorization-v3.component.html',
    styleUrls: ['./business-categorization-v3.component.scss'],
})
export class BusinessCategorizationV3Component extends BaseFormComponent implements OnInit, OnDestroy {
    readonly INVALID_INDEX = -10;
    readonly INVALID_STOREFRONT_IMAGE_CODE = '400079';
    readonly REVIEW_SUSPENDED_CODE = '400072';
    readonly CATEGORY_STEPS = BUSINESS_CATEGORY_STEPS;
    readonly ModalTypeEnum = ModalType;
    readonly MIN_JUSTIFICATION_NOTE_LENGTH = 10;
    readonly MAX_JUSTIFICATION_NOTE_LENGTH = 250;

    @Input() businessType: string;
    @Input() agentId: number;
    @Input() canUpdate = true;
    @Output() onPageChange = new EventEmitter();

    loading = true;
    loadingCategories = true;
    loadingSubCategories = true;
    showStoreFrontSample = false;
    showStoreFrontImageUpload = false;
    showDetailsModal = false;
    isSavingChanges = false;
    isLoadingStorefrontImg = false;
    showUploadImageErrorModal = false;
    isUnderReview = true;
    isPOSServiceSelected = false;

    currentStep = BUSINESS_CATEGORY_STEPS.CATEGORIES_LIST;
    selectedCategory: number;

    justificationNote = '';
    categoriesControl: CustomFormControlDefinition;
    subCategoryControl: CustomFormControlDefinition;

    categories: SelectAdaptor[] = [];
    subCategories: SelectAdaptor[] = [];

    businessAgentId: number;
    associatedBusinessTypes: Array<AssociatedBusinessType>;

    tempStoreFrontImgRef: string;
    storeFrontImgRef: string;
    storeFrontImg: string;

    businessClassification: string;
    errorMessage: string;
    form: FormGroup;
    $formSubscription = new Subject();

    categoriesFormList = [];
    editModeIndex = this.INVALID_INDEX;
    posBusinessInfo = null;

    isMobileView: boolean;

    uploadImageError = 'Store front image is invalid';
    reviewWarning: string;

    classificationDefinitions = [
        {
            icon: 'assets/images/svgs/pos-icon-outlined.svg',
            title: 'Agent',
            description:
                'Agents exclusively conducts POS/Agency Banking services. They facilitate financial transactions such as deposits, withdrawals, and bill payments for customers.',
        },
        {
            icon: 'assets/images/svgs/merchant-icon-outlined.svg',
            title: 'Merchant',
            description:
                'Merchants don’t engage in POS/Agency Banking but operates other types of businesses. They focus on retail sales/service-based offerings without involving agency banking services.',
        },
    ];

    constructor(
        private fb: FormBuilder,
        private businessService: BusinessService,
        private modalService: ModalService,
        private localStorage: LocalStorageService,
        private config: AppConfig,
        private screenManager: ScreenManagerService,
        private dashboardService: DashboardService,
        private utilityService: UtilityService,
        private onboardingService: OnboardingV2Service,
        private dataLayerService: DataLayerService,
    ) {
        super(fb);
    }

    get maxStoreFrontFileSize() {
        return this.config.STORE_FRONT_IMAGE_MAX_SIZE;
    }

    get maxAllowedBusinessTypes() {
        return this.config.MAX_ALLOWED_BUSINESS_TYPES;
    }

    get hasLoadedCategories() {
        return this.categories?.length > 0;
    }

    get isUnclassified() {
        return this.businessClassification?.toUpperCase?.() === BusinessClassification.UNCLASSIFIED;
    }

    get isAgent() {
        return !this.isUnclassified && this.businessClassification?.toUpperCase?.() === BusinessClassification.AGENT;
    }

    get showProvideJustification() {
        if (this.isUnderReview) {
            return false;
        }

        return this.businessClassification?.toUpperCase?.() === BusinessClassification.AGENT;
    }

    get isValidJustification() {
        if (!this.showProvideJustification) return true;

        if (StringHelper.isNullOrEmptyOrUndefined(this.justificationNote)) {
            return false;
        }

        const noteChars = this.justificationNote?.length;
        return noteChars >= this.MIN_JUSTIFICATION_NOTE_LENGTH && noteChars <= this.MAX_JUSTIFICATION_NOTE_LENGTH;
    }

    get hasCategoriesChanged() {
        if (this.associatedBusinessTypes?.length !== this.categoriesFormList.length) {
            return true;
        }
        const isSameTypes = this.associatedBusinessTypes?.every((category) =>
            this.categoriesFormList.some((businessType) => businessType.businessTypeId === category.businessTypeId),
        );
        return !isSameTypes;
    }

    get isSubmitDisabled() {
        const hasImage = Boolean(this.tempStoreFrontImgRef || this.storeFrontImgRef);
        const hasFormUpdated = !!this.tempStoreFrontImgRef || this.hasCategoriesChanged;
        const isValidForm = this.categoriesFormList.length > 0 && hasImage;

        return !(isValidForm && hasFormUpdated && this.isValidJustification);
    }

    get showAddNewCategory() {
        const selectedCategories = this.categoriesFormList ?? [];
        const posBusinessTypeId = this.posBusinessInfo?.businessTypeId;
        const isApiRunning = !posBusinessTypeId;
        const isDataProcessing = this.loading || this.isUnderReview;
        if (isDataProcessing || isApiRunning) {
            return false;
        }

        return !selectedCategories.some((category) => category.businessTypeId === posBusinessTypeId);
    }

    get disableUpdateCategory() {
        this.isPOSServiceSelected = false;
        if (!this.canUpdate || !this.form.valid) {
            return true;
        }

        const selectedCategories = this.categoriesFormList.length;
        const isEditing = selectedCategories === 1 && this.editModeIndex !== this.INVALID_INDEX;
        if (selectedCategories === 0 || isEditing) {
            return false;
        }

        const formValue = this.form.get('businessTypeId').value;
        const businessTypeId = this.subCategories.find((subCategory) => subCategory.label === formValue)?.value;

        this.isPOSServiceSelected = businessTypeId === this.posBusinessInfo?.businessTypeId;
        return this.isPOSServiceSelected;
    }

    get nextClassification() {
        return this.isAgent ? BusinessClassification.MERCHANT : BusinessClassification.AGENT;
    }

    get showExistingImage() {
        if (this.isUnderReview) {
            return true;
        }

        const showImagePreview = this.storeFrontImgRef && !this.showStoreFrontImageUpload;
        return showImagePreview;
    }

    get showUploadImage() {
        if (this.isUnderReview) {
            return false;
        }

        return !this.storeFrontImgRef || this.showStoreFrontImageUpload;
    }

    get showUploadImageSection() {
        if (this.loading) {
            return false;
        }

        if (this.isUnderReview) {
            return !!this.storeFrontImgRef;
        }

        return true;
    }

    setForm() {
        this.form = this.fb.group({
            businessId: [null, [Validators.required]],
            businessTypeId: [null, [Validators.required]],
        });
        this.onCategoryChange();
    }

    ngOnInit(): void {
        this.onPageChange.emit('Business Type ');
        this.isMobileView = this.screenManager.isMobile();

        this.loadInitialData();
    }

    ngOnDestroy() {
        this.$formSubscription.next('');
        this.$formSubscription.complete();
    }

    setInitialData() {
        if (!this.businessAgentId) return;

        this.dashboardService
            .getBusinessCategoriesForBrm(this.businessAgentId)
            .then((res: BusinessTypesModal) => {
                this.updateInitialAssociatedBusinessTypes(res.associatedBusinessTypes);
                this.updateInitialStoreFrontImage(res.storeFrontImageReference);
                this.updateInitialBusinessClassification(res.businessClassification);
            })
            .catch((err) => {
                logger.error('Error during getting initial business categories and image reference:', err);
                this.setAssociatedBusinessTypes();
                this.setStoreFrontImage();
                this.setBusinessClassification();
            })
            .finally(() => {
                this.loading = false;
            });
    }

    updateInitialAssociatedBusinessTypes(associatedBusinessTypes: AssociatedBusinessType[] = []) {
        if (!associatedBusinessTypes.length) {
            return this.setAssociatedBusinessTypes();
        }

        this.categoriesFormList = [];
        associatedBusinessTypes.forEach((associatedBusinessType) => {
            this.categoriesFormList.push({ ...associatedBusinessType });
        });
        this.associatedBusinessTypes = [...this.categoriesFormList];
    }

    updateInitialStoreFrontImage(storeFrontImageReference: string) {
        if (!storeFrontImageReference) {
            return this.setStoreFrontImage();
        }

        this.storeFrontImgRef = storeFrontImageReference;
        this.fetchStoreFrontImg(this.storeFrontImgRef);
    }

    updateInitialBusinessClassification(businessClassification: BusinessClassification) {
        if (!businessClassification) {
            return this.setBusinessClassification();
        }

        this.businessClassification = businessClassification ?? this.businessType ?? '';
        if (!this.agentId) {
            this.localStorage.save(StorageKeys.customer_business_type, businessClassification);
        }
    }

    setBusinessAgentId() {
        this.businessAgentId = this.agentId ?? this.localStorage.get(StorageKeys.businessId);
    }

    loadInitialData() {
        this.loading = true;
        this.setBusinessAgentId();
        this.getBusinessTypesStatus();
        this.setInitialData();
        this.loading = false;
    }

    setAssociatedBusinessTypes() {
        const associatedBusinessTypes = this.localStorage.get(StorageKeys.associated_business_types);

        if (!associatedBusinessTypes) return;
        const parsedBusinessTypesArray = JSON.parse(associatedBusinessTypes);

        this.associatedBusinessTypes = parsedBusinessTypesArray ?? [];
        this.categoriesFormList = [...this.associatedBusinessTypes];
    }

    setBusinessClassification() {
        this.businessClassification = this.localStorage.get(StorageKeys.customer_business_type) ?? this.businessType;
    }

    setStoreFrontImage() {
        const imageReference = this.localStorage.get(StorageKeys.store_front_image_reference);
        if (!imageReference) {
            this.storeFrontImgRef = '';
            this.storeFrontImg = '';
            return;
        }

        this.storeFrontImgRef = imageReference;
        this.fetchStoreFrontImg(imageReference);
    }

    async getPOSBusinessInfoFromConfig() {
        const config = await this.utilityService.getConfig();
        this.posBusinessInfo = config?.posBusinessTypeInfo;
    }

    handleOpenDetailsModal() {
        this.showDetailsModal = true;
    }

    handleCloseDetailsModal() {
        this.showDetailsModal = false;
    }

    handleCloseAlertModal() {
        this.showUploadImageErrorModal = false;
        this.tempStoreFrontImgRef = undefined;
    }

    fetchStoreFrontImg(imageReference?: string) {
        if (!imageReference) return;

        this.isLoadingStorefrontImg = true;
        if (this.storeFrontImgRef === this.tempStoreFrontImgRef) {
            this.showStoreFrontImageUpload = false;
        }
        this.dashboardService
            .getFileInfo(imageReference)
            .then((res) => {
                this.storeFrontImg = res.base64File;
            })
            .catch((err) => {
                StringHelper.getErrorMessageHttpErrorResponse(err);
            })
            .finally(() => {
                this.isLoadingStorefrontImg = false;
            });
    }

    handleSaveCategory() {
        const subCategory = this.form.get('businessTypeId').value;
        const allSubcategoryIds = this.categoriesFormList.map((category) => category.businessTypeId);

        const category = {
            name: subCategory,
            businessCategoryName: this.form.get('businessId').value,
            businessTypeId: this.subCategories.find((_subCategory) => _subCategory.label === subCategory)?.value,
        };

        const isInvalidIndex = this.editModeIndex === this.INVALID_INDEX;
        const isDuplicate = allSubcategoryIds.includes(category.businessTypeId) && isInvalidIndex;
        const indexOfExisting = allSubcategoryIds.indexOf(category.businessTypeId);
        const isSameAsEdit = indexOfExisting > -1 && indexOfExisting !== this.editModeIndex;

        if (isSameAsEdit || isDuplicate) {
            this.errorMessage = 'This business type already exists. Please select a different business type.';
            return;
        }

        if (this.editModeIndex > this.INVALID_INDEX) {
            this.categoriesFormList = this.categoriesFormList.map((eCategory, current) => (current === this.editModeIndex ? category : eCategory));

            this.editModeIndex = this.INVALID_INDEX;
        } else {
            this.categoriesFormList.push(category);
        }

        this.changeTempToPreviewStoreFront();
        this.currentStep = BUSINESS_CATEGORY_STEPS.CATEGORIES_LIST;
    }

    toggleSample() {
        this.showStoreFrontSample = !this.showStoreFrontSample;
    }

    switchToImageUpload() {
        this.storeFrontImgRef = '';
        this.storeFrontImg = '';
        this.tempStoreFrontImgRef = '';
        this.showStoreFrontImageUpload = true;
    }

    handleGetFile($event) {
        this.tempStoreFrontImgRef = $event.fileReference;
    }

    handleLogUploadEvent(status: LogEventStatus, error?: LogErrorEventType) {
        const businessId = this.localStorage.get(StorageKeys.businessId);
        const payload = { business_id: businessId };

        if (this.tempStoreFrontImgRef) {
            switch (status) {
                case LogEventStatus.INITIATED:
                    this.dataLayerService.logEventWithData(StorefrontUploadEvents.initiated, payload);
                    return;
                case LogEventStatus.SUCCESS:
                    this.dataLayerService.logEventWithData(StorefrontUploadEvents.success, payload);
                    return;
                case LogEventStatus.FAILED:
                    this.dataLayerService.logEventWithData(StorefrontUploadEvents.failed, {
                        ...payload,
                        api_error_code: error?.error?.code,
                        api_error_message: error?.error?.description,
                    });
                    return;
            }
        }
    }

    getBusinessTypesStatus() {
        this.businessService
            .getBusinessTypesStatus(this.businessAgentId)
            .then(() => {
                this.isUnderReview = false;
                this.reviewWarning = '';
            })
            .catch((error) => {
                const errorText = StringHelper.getErrorMessageHttpErrorResponse(error);

                if (error.error?.code === this.REVIEW_SUSPENDED_CODE) {
                    this.isUnderReview = true;
                    this.reviewWarning = errorText;
                    return;
                }

                this.isUnderReview = false;
                this.reviewWarning = '';
            })
            .finally(() => {
                if (this.isUnderReview) return;

                this.getPOSBusinessInfoFromConfig();
                this.getBusinessCategories();
            });
    }

    async handleSubmit() {
        this.modalService.closeAll();

        const businessTypeIds = this.categoriesFormList.map((category) => category?.businessTypeId);
        const businessId = this.agentId ?? this.localStorage.get(StorageKeys.businessId);

        if (!businessId) {
            return this.alertError('businessId or business code missing.');
        }

        const storeFrontImageReference = this.tempStoreFrontImgRef ?? this.storeFrontImgRef;
        if (!storeFrontImageReference) {
            return this.alertError('Please upload a clear photo of your shopfront including the signpost.');
        }

        this.handleLogUploadEvent(LogEventStatus.INITIATED);
        this.isSavingChanges = true;
        try {
            const payload = {
                businessId,
                businessTypeIds,
                storeFrontImageReference,
                justificationNote: this.showProvideJustification ? this.justificationNote : undefined,
            };
            const response = await this.businessService.saveBusinessTypesV3(payload);

            this.justificationNote = '';
            this.handleLogUploadEvent(LogEventStatus.SUCCESS);
            this.alertSuccess(response?.message ?? 'Changes saved successfully');
        } catch (error) {
            if (error?.error?.code === this.INVALID_STOREFRONT_IMAGE_CODE) {
                this.handleInvalidStoreImageUpload(error);
            } else {
                this.alertError(StringHelper.getErrorMessageHttpErrorResponse(error));
            }
        } finally {
            this.isSavingChanges = false;
        }
    }

    handleInvalidStoreImageUpload(error) {
        this.handleLogUploadEvent(LogEventStatus.FAILED, error);
        this.uploadImageError = StringHelper.getErrorMessageHttpErrorResponse(error) ?? 'Store front image is invalid';
        this.showUploadImageErrorModal = true;
    }

    onCategoryChange(): void {
        this.form
            .get('businessId')
            .valueChanges.pipe(takeUntil(this.$formSubscription))
            .subscribe((val) => {
                if (!val) {
                    return;
                }

                this.selectedCategory = this.categories.find((category) => category.label === val)?.value ?? '';
                this.form.controls['businessTypeId'].setValue('');

                this.getBusinessSubCategories();
            });
    }

    getBusinessCategories() {
        this.loadingCategories = true;

        this.onboardingService
            .getBusinessCategories()
            .then((response) => {
                this.categories = response.map((category) => {
                    category['label'] = category.name;
                    category['value'] = category.id;
                    return category;
                });
            })
            .catch((error) => {
                this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(error);
            })
            .finally(() => {
                this.loadingCategories = false;
            });
    }

    getBusinessSubCategories() {
        this.subCategories = [];
        if (!this.selectedCategory) return;

        this.loadingSubCategories = true;
        this.businessService
            .getBusinessSubCategories(this.selectedCategory)
            .then((res) => {
                this.subCategories = res.map((subCategory) => {
                    subCategory['label'] = subCategory.name;
                    subCategory['value'] = subCategory.id;
                    return subCategory;
                });
            })
            .catch((error) => {
                this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(error);
            })
            .finally(() => {
                this.loadingSubCategories = false;
            });
    }

    showMaxCategoriesAlert() {
        const config = new ModalOptionsConfig();
        config.nzComponentParams.alertType = 'info';
        config.nzComponentParams.title = 'Selection Limit Notice';
        config.nzComponentParams.message = `You can select up to ${this.maxAllowedBusinessTypes} business categories. Need help? Contact customer support.`;
        config.nzComponentParams.callback = () => {
            this.modalService.closeAll();
        };
        config.className = DEFAULT_DIALOG_CLASSES;
        config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
        config.nzComponentParams.callbackName = 'Dismiss';
        this.modalService.alert(config);
    }

    alertSuccess(message: string) {
        const config = new ModalOptionsConfig();
        config.nzComponentParams.alertType = 'success';
        config.nzComponentParams.title = 'Success';
        config.nzComponentParams.message = message;
        config.className = DEFAULT_DIALOG_CLASSES;
        config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
        config.nzComponentParams.callback = () => {
            this.loadInitialData();
            this.currentStep = BUSINESS_CATEGORY_STEPS.CATEGORIES_LIST;
        };
        this.modalService.alert(config);
    }

    alertError(error: string) {
        const config = new ModalOptionsConfig();
        config.nzComponentParams.alertType = 'error';
        config.nzComponentParams.title = 'Invalid Category Selection';
        config.nzComponentParams.message = error;
        config.className = DEFAULT_DIALOG_CLASSES;
        config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
        this.modalService.alert(config);
    }

    handleEditCategory(index: number) {
        if (!this.categoriesFormList?.length || this.isUnderReview) return;

        this.errorMessage = '';
        const selected = this.categoriesFormList.find((_, idx) => idx === index);
        if (!selected) return;

        this.selectedCategory = this.categories.find((category) => category.label === selected.businessCategoryName)?.value;

        this.form = this.fb.group({
            businessId: [selected.businessCategoryName, [Validators.required]],
            businessTypeId: [selected.name, [Validators.required]],
        });

        this.editModeIndex = index;
        this.onCategoryChange();
        this.getBusinessSubCategories();

        this.currentStep = BUSINESS_CATEGORY_STEPS.ADD_EDIT_CATEGORY;
    }

    handleCreateCategory() {
        if (this.categoriesFormList.length >= this.maxAllowedBusinessTypes) {
            return this.showMaxCategoriesAlert();
        }
        this.setForm();
        this.errorMessage = '';

        this.currentStep = BUSINESS_CATEGORY_STEPS.ADD_EDIT_CATEGORY;
    }

    handleDeleteCategory(index: number) {
        if (this.isUnderReview) return;

        this.categoriesFormList = this.categoriesFormList.filter((_, current) => current !== index);
    }

    handleBack() {
        this.changeTempToPreviewStoreFront();
        this.editModeIndex = this.INVALID_INDEX;
        this.currentStep = BUSINESS_CATEGORY_STEPS.CATEGORIES_LIST;
    }

    changeTempToPreviewStoreFront() {
        if (this.tempStoreFrontImgRef) {
            this.storeFrontImgRef = this.tempStoreFrontImgRef;
            this.fetchStoreFrontImg(this.tempStoreFrontImgRef);
        }
    }
}
